import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PublicProfileCard from "@/components/PublicProfileCard";
import { toast } from "sonner";
import {
  Users, User as UserIcon, Trash2, Download, Loader2, Shuffle, Wand2,
  LayoutGrid, Heart, Bookmark, CheckSquare, Square, X as XIcon, Check, Send,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { styleLabel } from "@/lib/styles";
import JSZip from "jszip";

const hasBlend = (g) => g && g.style_b && g.blend != null && g.style_b !== g.style;
const isTurnaround = (g) => g && g.type === "turnaround" && g.views && Object.keys(g.views).length >= 2;
const TURN_VIEWS = ["front", "back", "left", "right"];
const TURN_LABELS = { front: "FRONT", back: "BACK", left: "LEFT", right: "RIGHT" };

const downloadUrl = async (url, filename) => {
  try {
    const res = await fetch(url, { credentials: "include" });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(link.href), 5000);
  } catch (e) {
    // Fallback: open in new tab
    window.open(url, "_blank");
  }
};

const promptSignIn = () => {
  const redirectUrl = window.location.origin + "/gallery";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Gallery() {
  const { user } = useAuth();
  const [scope, setScope] = useState(user ? "mine" : "public");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const navigate = useNavigate();

  // Force guests to public scope
  useEffect(() => {
    if (!user && scope !== "public") setScope("public");
  }, [user]); // eslint-disable-line

  const remix = (g) => {
    if (!user) return promptSignIn();
    navigate("/studio", {
      state: { remix: { style: g.style, style_b: g.style_b, blend: g.blend, prompt: g.prompt } },
    });
  };

  const sendToPrompt = (g) => {
    if (!user) return promptSignIn();
    if (!g?.image_url) { toast.error("This item has no image to attach."); return; }
    // Route through the same `attachRef` handoff the Timeline Editor uses —
    // Studio pushes it straight onto the omni-reference rail (prompt-box
    // image attachment slot), same shape as a drag-drop into the prompt.
    navigate("/studio", {
      state: {
        attachRef: {
          generation_id: g.generation_id,
          image_url: g.image_url,
          source: "gallery-send-to-prompt",
        },
      },
    });
  };

  const fetchItems = async (s) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const { data } = await api.get(`/generations?scope=${s}`);
      setItems(data);
    } catch (e) {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(scope); }, [scope]);

  const remove = async (id) => {
    try {
      await api.delete(`/generations/${id}`);
      setItems((it) => it.filter((g) => g.generation_id !== id));
      setPreview(null);
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleVis = async (item) => {
    try {
      const { data } = await api.patch(`/generations/${item.generation_id}/visibility`, {
        is_public: !item.is_public,
      });
      setItems((its) => its.map((g) => (g.generation_id === item.generation_id ? { ...g, is_public: data.is_public } : g)));
      if (preview?.generation_id === item.generation_id) setPreview({ ...preview, is_public: data.is_public });
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleVote = async (item, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!user) { promptSignIn(); return; }
    try {
      const { data } = await api.post(`/generations/${item.generation_id}/vote`);
      setItems((its) => its.map((g) => (g.generation_id === item.generation_id ? { ...g, votes: data.votes, voted_by_me: data.voted } : g)));
      if (preview?.generation_id === item.generation_id) setPreview({ ...preview, votes: data.votes, voted_by_me: data.voted });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vote failed");
    }
  };

  const toggleSave = async (item, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!user) { promptSignIn(); return; }
    try {
      const { data } = await api.post(`/generations/${item.generation_id}/save`);
      const nowSaved = data.saved;
      setItems((its) => its.map((g) => (g.generation_id === item.generation_id ? { ...g, saved_by_me: nowSaved } : g)));
      if (preview?.generation_id === item.generation_id) setPreview({ ...preview, saved_by_me: nowSaved });
      toast.success(nowSaved ? "Saved to your collection" : "Removed from saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const tapCard = (g, e) => {
    if (selectMode) {
      e.preventDefault();
      const next = new Set(selected);
      next.has(g.generation_id) ? next.delete(g.generation_id) : next.add(g.generation_id);
      setSelected(next);
      return;
    }
    setPreview(g);
  };

  const toggleSelectMode = () => {
    if (!user) { promptSignIn(); return; }
    setSelectMode((v) => { if (v) setSelected(new Set()); return !v; });
  };

  const selectAll = () => setSelected(new Set(items.map((g) => g.generation_id)));
  const selectNone = () => setSelected(new Set());

  const selectedItems = useMemo(
    () => items.filter((g) => selected.has(g.generation_id)),
    [items, selected]
  );

  // Bulk save: saves each PUBLIC selected item to the user's collection
  const bulkSave = async () => {
    if (!user) return promptSignIn();
    if (selectedItems.length === 0) return;
    setBulkBusy(true);
    let ok = 0, skip = 0;
    for (const g of selectedItems) {
      // Skip if it's the user's own item (already in their vault)
      if (g.user_id === user.user_id) { skip += 1; continue; }
      if (g.saved_by_me) continue;
      try {
        await api.post(`/generations/${g.generation_id}/save`);
        ok += 1;
      } catch { /* skip on failure */ }
    }
    setBulkBusy(false);
    toast.success(`Saved ${ok} item${ok === 1 ? "" : "s"} to your collection${skip ? ` (${skip} skipped — already yours)` : ""}.`);
    fetchItems(scope);
    setSelectMode(false); setSelected(new Set());
  };

  // Bulk delete: only for items owned by the user
  const bulkDelete = async () => {
    if (!user) return promptSignIn();
    const own = selectedItems.filter((g) => (g.user_id === user.user_id) || scope === "mine");
    if (own.length === 0) { toast.info("Nothing to delete — you can only delete your own creations."); return; }
    if (!window.confirm(`Delete ${own.length} generation${own.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    let ok = 0;
    for (const g of own) {
      try {
        await api.delete(`/generations/${g.generation_id}`);
        ok += 1;
      } catch { /* skip */ }
    }
    setBulkBusy(false);
    setItems((it) => it.filter((g) => !own.some((o) => o.generation_id === g.generation_id)));
    setSelectMode(false); setSelected(new Set());
    toast.success(`Deleted ${ok} generation${ok === 1 ? "" : "s"}.`);
  };

  // Bulk download: single file → direct; multiple → zip
  const bulkDownload = async () => {
    if (selectedItems.length === 0) return;
    setBulkBusy(true);
    try {
      if (selectedItems.length === 1) {
        const g = selectedItems[0];
        const url = g.video_url || g.image_url;
        const ext = g.video_url ? "mp4" : "png";
        await downloadUrl(url, `geneo-ai-${g.generation_id}.${ext}`);
      } else {
        const zip = new JSZip();
        for (const g of selectedItems) {
          const url = g.video_url || g.image_url;
          if (!url) continue;
          try {
            const res = await fetch(url, { credentials: "include" });
            const blob = await res.blob();
            const ext = g.video_url ? "mp4" : ((blob.type.split("/")[1] || "png").split(";")[0]);
            zip.file(`geneo-ai-${g.generation_id}.${ext}`, blob);
          } catch { /* skip */ }
        }
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(zipBlob);
        link.download = `geneo-ai-vault-${Date.now()}.zip`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 5000);
      }
      toast.success(`Downloading ${selectedItems.length} item${selectedItems.length === 1 ? "" : "s"}…`);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] glyph-bg pb-32">
      <Navbar />

      {/* Guest banner */}
      {!user && (
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-6">
          <div
            data-testid="gallery-guest-banner"
            className="rounded-2xl border border-[#F2C94C]/25 bg-gradient-to-r from-[#120E15] via-[#0A0A0A] to-[#120E15] p-4 flex items-center gap-4 flex-wrap"
          >
            <div className="flex-1 min-w-0">
              <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-1">// GUEST · PUBLIC DYNASTY</div>
              <div className="font-body text-sm text-white">
                You're browsing what the community created. <span className="text-white">Sign in to save, vote, remix, and start making your own.</span>
              </div>
            </div>
            <button
              onClick={promptSignIn}
              data-testid="gallery-guest-signin-btn"
              className="font-mono-hy text-[10px] px-5 py-3 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all hover:-translate-y-0.5 flex items-center gap-2 glow-gold"
            >
              Sign in with Google
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="font-mono-hy text-[11px] text-[#F2C94C] mb-2">// Gallery</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight">The Vault</h1>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-2 p-1 rounded-full bg-[#120E15] border border-[#F2C94C]/15">
              {user && (
                <>
                  <button
                    onClick={() => setScope("mine")}
                    data-testid="scope-mine-btn"
                    className={`font-mono-hy text-[10px] px-5 py-2 rounded-full transition-all flex items-center gap-2 ${
                      scope === "mine" ? "bg-[#F2C94C] text-[#0A0A0A]" : "text-white"
                    }`}
                  >
                    <UserIcon className="w-3.5 h-3.5" /> My Vault
                  </button>
                  <button
                    onClick={() => setScope("saved")}
                    data-testid="scope-saved-btn"
                    className={`font-mono-hy text-[10px] px-5 py-2 rounded-full transition-all flex items-center gap-2 ${
                      scope === "saved" ? "bg-[#F2C94C] text-[#0A0A0A]" : "text-white"
                    }`}
                  >
                    <Bookmark className="w-3.5 h-3.5" /> Saved
                  </button>
                </>
              )}
              <button
                onClick={() => setScope("public")}
                data-testid="scope-public-btn"
                className={`font-mono-hy text-[10px] px-5 py-2 rounded-full transition-all flex items-center gap-2 ${
                  scope === "public" ? "bg-[#F2C94C] text-[#0A0A0A]" : "text-white"
                }`}
              >
                <Users className="w-3.5 h-3.5" /> Public Dynasty
              </button>
            </div>

            {/* Select mode toggle — logged-in only */}
            {user && items.length > 0 && (
              <button
                onClick={toggleSelectMode}
                data-testid="gallery-select-toggle"
                className={`font-mono-hy text-[10px] px-4 py-2 rounded-full border transition-all flex items-center gap-2 ${
                  selectMode
                    ? "bg-[#F2C94C] text-[#0A0A0A] border-[#F2C94C]"
                    : "border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10"
                }`}
              >
                {selectMode ? <><XIcon className="w-3.5 h-3.5" /> Exit select</> : <><CheckSquare className="w-3.5 h-3.5" /> Select</>}
              </button>
            )}
          </div>
        </div>

        {/* Selection sub-toolbar */}
        {selectMode && (
          <div className="mb-4 flex items-center gap-3 flex-wrap font-mono-hy text-[10px]" data-testid="gallery-select-toolbar">
            <span className="text-[#F2C94C]">// {selected.size} selected</span>
            <button onClick={selectAll} data-testid="gallery-select-all" className="px-3 py-1.5 rounded-full border border-[#F2C94C]/25 text-white hover:border-[#F2C94C]">Select all</button>
            <button onClick={selectNone} data-testid="gallery-select-none" className="px-3 py-1.5 rounded-full border border-[#F2C94C]/25 text-white hover:text-white">Clear</button>
          </div>
        )}

        {user && scope === "mine" && !selectMode && (
          <PublicProfileCard />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-8 h-8 text-[#F2C94C] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-32">
            <div className="font-mono-hy text-[10px] text-white mb-2">// Empty</div>
            <p className="font-display text-2xl text-white">
              {scope === "mine"
                ? "Create your first vision in the Studio."
                : scope === "saved"
                ? "Save public creations you love to build your own collection."
                : "The dynasty is quiet. Be the first."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((g) => {
              const isSelected = selected.has(g.generation_id);
              return (
                <button
                  key={g.generation_id}
                  onClick={(e) => tapCard(g, e)}
                  data-testid={`gallery-item-${g.generation_id}`}
                  className={`group relative aspect-[3/4] rounded-2xl overflow-hidden border transition-all duration-500 hover:-translate-y-1 ${
                    isSelected ? "border-[#F2C94C] ring-2 ring-[#F2C94C]/40" : "border-[#F2C94C]/10 hover:border-[#F2C94C]/50"
                  }`}
                >
                  {g.video_url ? (
                    <video src={g.video_url} muted loop playsInline autoPlay className="w-full h-full object-cover" />
                  ) : g.turntable_video_url ? (
                    <video src={g.turntable_video_url} muted loop playsInline autoPlay className="w-full h-full object-cover" />
                  ) : (
                    <img src={g.image_url || g.image_data} alt={g.prompt} className="w-full h-full object-cover" loading="lazy" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="font-body text-xs text-white truncate">{g.prompt}</div>
                    {g.author_name && <div className="font-mono-hy text-[9px] text-[#F2C94C] mt-1">— {g.author_name}</div>}
                  </div>

                  {/* Selection checkbox */}
                  {selectMode && (
                    <div
                      data-testid={`select-check-${g.generation_id}`}
                      className={`absolute top-2 left-2 w-7 h-7 rounded-lg flex items-center justify-center backdrop-blur-md border transition-all ${
                        isSelected ? "bg-[#F2C94C] border-[#F2C94C]" : "bg-[#0A0A0A]/80 border-[#F2C94C]/50"
                      }`}
                    >
                      {isSelected ? <Check className="w-4 h-4 text-[#0A0A0A]" /> : <Square className="w-4 h-4 text-white" />}
                    </div>
                  )}

                  {g.video_url && !selectMode && (
                    <div className="absolute top-2 right-2 font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#00E5FF]/90 text-[#0A0A0A]">
                      {g.type === "story_reel" ? "★ REEL · 15s" : "VIDEO"}
                    </div>
                  )}
                  {!selectMode && isTurnaround(g) && g.turntable_video_url && (
                    <div className="absolute top-2 right-2 font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#00E5FF]/90 text-[#0A0A0A] flex items-center gap-1">
                      ⟳ 360°
                    </div>
                  )}
                  {!selectMode && isTurnaround(g) && !g.turntable_video_url && !g.video_url && (
                    <div className="absolute top-2 right-2 font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#F2C94C]/90 text-[#0A0A0A] flex items-center gap-1">
                      <LayoutGrid className="w-2.5 h-2.5" /> 4·VIEWS
                    </div>
                  )}

                  {/* Vote / Save badges (public scope only) */}
                  {scope === "public" && !selectMode && (
                    <div className="absolute top-2 left-2 flex flex-col gap-1.5">
                      <div
                        onClick={(e) => toggleVote(g, e)}
                        role="button"
                        data-testid={`vote-btn-${g.generation_id}`}
                        className={`font-mono-hy text-[10px] px-2.5 py-1 rounded-full backdrop-blur-md flex items-center gap-1.5 cursor-pointer border transition-all ${
                          g.voted_by_me
                            ? "bg-[#E27D60] border-[#E27D60] text-white"
                            : "bg-[#0A0A0A]/80 border-[#F2C94C]/30 text-white hover:border-[#E27D60] hover:text-[#E27D60]"
                        }`}
                      >
                        <Heart className={`w-3 h-3 ${g.voted_by_me ? "fill-current" : ""}`} />
                        <span className="font-bold">{g.votes || 0}</span>
                      </div>
                      <div
                        onClick={(e) => toggleSave(g, e)}
                        role="button"
                        data-testid={`save-btn-${g.generation_id}`}
                        className={`font-mono-hy text-[10px] px-2.5 py-1 rounded-full backdrop-blur-md flex items-center gap-1.5 cursor-pointer border transition-all ${
                          g.saved_by_me
                            ? "bg-[#F2C94C] border-[#F2C94C] text-[#0A0A0A]"
                            : "bg-[#0A0A0A]/80 border-[#F2C94C]/30 text-white hover:border-[#F2C94C] hover:text-[#F2C94C]"
                        }`}
                      >
                        <Bookmark className={`w-3 h-3 ${g.saved_by_me ? "fill-current" : ""}`} />
                      </div>
                    </div>
                  )}

                  {scope === "mine" && !selectMode && g.is_public && (
                    <div className="absolute top-2 left-2 font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#27AE60]/90 text-[#0A0A0A]">PUBLIC</div>
                  )}

                  {hasBlend(g) && !selectMode && (
                    <div
                      data-testid={`blend-badge-${g.generation_id}`}
                      className="absolute bottom-2 left-2 right-2 font-mono-hy text-[9px] px-2.5 py-1.5 rounded-full bg-[#0A0A0A]/80 backdrop-blur-md border border-[#00E5FF]/40 text-white flex items-center gap-1.5 justify-center opacity-100 group-hover:opacity-0 transition-opacity duration-300"
                    >
                      <Shuffle className="w-2.5 h-2.5 text-[#00E5FF] shrink-0" />
                      <span className="truncate">
                        <span className="text-[#F2C94C]">{g.blend}%</span> {styleLabel(g.style)}
                        <span className="text-white"> × </span>
                        <span className="text-[#00E5FF]">{100 - g.blend}%</span> {styleLabel(g.style_b)}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating bulk action bar */}
      {selectMode && selected.size > 0 && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-40" data-testid="gallery-bulk-actionbar">
          <div className="flex items-center gap-2 p-2 rounded-full bg-[#120E15] border border-[#F2C94C]/25 shadow-2xl backdrop-blur-lg">
            <span className="font-mono-hy text-[10px] text-[#F2C94C] px-3">
              {selected.size} selected
            </span>
            <button
              onClick={bulkSave}
              disabled={bulkBusy}
              data-testid="gallery-bulk-save-btn"
              className="font-mono-hy text-[10px] px-4 py-2.5 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] disabled:opacity-60 transition-all flex items-center gap-2"
            >
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bookmark className="w-3.5 h-3.5" />} Save
            </button>
            <button
              onClick={bulkDownload}
              disabled={bulkBusy}
              data-testid="gallery-bulk-download-btn"
              className="font-mono-hy text-[10px] px-4 py-2.5 rounded-full bg-[#00E5FF] text-[#0A0A0A] hover:bg-[#7BF0FF] disabled:opacity-60 transition-all flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </button>
            <button
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="gallery-bulk-delete-btn"
              className="font-mono-hy text-[10px] px-4 py-2.5 rounded-full border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60]/10 disabled:opacity-60 transition-all flex items-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#120E15] border-[#F2C94C]/20 text-white max-w-3xl">
          {preview && (
            <>
              <div className="rounded-xl overflow-hidden bg-[#0A0A0A]">
                {preview.video_url ? (
                  <video src={preview.video_url} controls autoPlay loop className="w-full max-h-[70vh]" />
                ) : isTurnaround(preview) && preview.turntable_video_url ? (
                  <video src={preview.turntable_video_url} controls autoPlay loop className="w-full max-h-[70vh]" data-testid="preview-turntable-video" />
                ) : isTurnaround(preview) ? (
                  <div className="grid grid-cols-2 gap-1 p-1 bg-[#0A0A0A]" data-testid="preview-turnaround-grid">
                    {TURN_VIEWS.map((v) => {
                      const view = preview.views[v];
                      if (!view?.image_url) return null;
                      return (
                        <div key={v} className="relative aspect-square bg-[#120E15]">
                          <img src={view.image_url} alt={v} className="w-full h-full object-cover" />
                          <div className="absolute top-1.5 left-1.5 font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#0A0A0A]/85 backdrop-blur border border-[#F2C94C]/30 text-[#F2C94C]">
                            {TURN_LABELS[v]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <img src={preview.image_url || preview.image_data} alt={preview.prompt} className="w-full max-h-[70vh] object-contain" />
                )}
              </div>
              <div className="pt-4">
                <div className="font-mono-hy text-[9px] text-white mb-1">PROMPT</div>
                <p className="font-body text-sm text-white">{preview.prompt}</p>
                {isTurnaround(preview) && preview.bio && (
                  <div className="mt-3 p-3 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/15" data-testid="preview-bio-card">
                    <div className="font-mono-hy text-[9px] text-[#F2C94C] mb-1.5">◇ BIO · AUTO-GENERATED</div>
                    <p className="font-body text-sm text-white leading-relaxed">{preview.bio}</p>
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    {hasBlend(preview) ? (
                      <span
                        data-testid="preview-blend-badge"
                        className="font-mono-hy text-[10px] px-3 py-1.5 rounded-full bg-[#0A0A0A] border border-[#00E5FF]/40 text-white flex items-center gap-2"
                      >
                        <Shuffle className="w-3 h-3 text-[#00E5FF]" />
                        <span className="text-[#F2C94C]">{preview.blend}%</span> {styleLabel(preview.style)}
                        <span className="text-white">×</span>
                        <span className="text-[#00E5FF]">{100 - preview.blend}%</span> {styleLabel(preview.style_b)}
                      </span>
                    ) : (
                      <span className="font-mono-hy text-[10px] text-white uppercase">{styleLabel(preview.style)}</span>
                    )}
                    {preview.author_name && (
                      <span className="font-mono-hy text-[10px] text-[#F2C94C]">— {preview.author_name}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {scope === "public" && (
                      <>
                        <button
                          onClick={() => toggleVote(preview)}
                          data-testid="preview-vote-btn"
                          className={`font-mono-hy text-[10px] px-4 py-2 rounded-full flex items-center gap-2 transition-all ${
                            preview.voted_by_me
                              ? "bg-[#E27D60] text-white hover:bg-[#eb8a70]"
                              : "border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60]/10"
                          }`}
                        >
                          <Heart className={`w-3.5 h-3.5 ${preview.voted_by_me ? "fill-current" : ""}`} />
                          {preview.votes || 0}
                        </button>
                        <button
                          onClick={() => toggleSave(preview)}
                          data-testid="preview-save-btn"
                          className={`font-mono-hy text-[10px] px-4 py-2 rounded-full flex items-center gap-2 transition-all ${
                            preview.saved_by_me
                              ? "bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B]"
                              : "border border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C]/10"
                          }`}
                        >
                          <Bookmark className={`w-3.5 h-3.5 ${preview.saved_by_me ? "fill-current" : ""}`} />
                          {preview.saved_by_me ? "Saved" : "Save"}
                        </button>
                      </>
                    )}
                    {scope === "mine" && (
                      <label className="flex items-center gap-2 font-mono-hy text-[10px] text-white">
                        <Switch checked={!!preview.is_public} onCheckedChange={() => toggleVis(preview)} data-testid="preview-toggle-public" />
                        Public
                      </label>
                    )}
                    <button
                      onClick={() => remix(preview)}
                      data-testid="preview-remix-btn"
                      className={hasBlend(preview)
                        ? "font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#00E5FF] text-[#0A0A0A] hover:bg-[#7BF0FF] transition-all flex items-center gap-2 glow-cyan"
                        : "font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 flex items-center gap-2"
                      }
                    >
                      {hasBlend(preview) ? <><Shuffle className="w-3.5 h-3.5" /> Remix blend</> : <><Wand2 className="w-3.5 h-3.5" /> Remix</>}
                    </button>
                    {preview.image_url && !preview.video_url && preview.type !== "turnaround" && (
                      <button
                        onClick={() => sendToPrompt(preview)}
                        data-testid="preview-send-to-prompt-btn"
                        className="font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60]/10 transition-all flex items-center gap-2"
                      >
                        <Send className="w-3.5 h-3.5" /> Send to Prompt
                      </button>
                    )}
                    <button
                      onClick={() => downloadUrl(preview.video_url || preview.image_url, `geneo-ai-${preview.generation_id}.${preview.video_url ? "mp4" : "png"}`)}
                      data-testid="preview-download-btn"
                      className="font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#F2C94C]/20 hover:border-[#F2C94C] flex items-center gap-2"
                    >
                      <Download className="w-3.5 h-3.5" /> Download
                    </button>
                    {scope === "mine" && (
                      <button
                        onClick={() => remove(preview.generation_id)}
                        data-testid="preview-delete-btn"
                        className="font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#E27D60]/30 text-[#E27D60] hover:bg-[#E27D60]/10 flex items-center gap-2"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
